import { useEffect, useMemo, useState } from 'react';
import {
  makeStyles,
  Popover,
  RadioGroup,
  Radio,
  FormControlLabel, Button,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { useSelector } from 'react-redux';
import FadingText from '../../../TextFade';

export const SortAscIcon = () => (
  <div style={{ marginLeft: 2, height: 16 }}>
    <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none'
         stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
      <path d='M4 6l7 0'></path>
      <path d='M4 12l7 0'></path>
      <path d='M4 18l9 0'></path>
      <path d='M15 9l3 -3l3 3'></path>
      <path d='M18 6l0 12'></path>
    </svg>
  </div>
);

export const SortDescIcon = () => (
  <div style={{ margin: '0 2px', height: 16 }}>
    <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none'
         stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'>
      <path d='M4 6l9 0'></path>
      <path d='M4 12l7 0'></path>
      <path d='M4 18l7 0'></path>
      <path d='M15 15l3 3l3 -3'></path>
      <path d='M18 6l0 12'></path>
    </svg>
  </div>
);

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    flexDirection: 'column',
    padding: '14px 18px',
    maxHeight: 435,
    minWidth: 250,
    overflow: 'hidden',
  },
  modalTitle: {
    display: 'flex',
    flexDirection: 'column',
    fontSize: 14,
    fontWeight: 600,
    padding: '4px 0px 10px 0px',
    top: 0,
    backgroundColor: 'white',
    gap: 10,
    zIndex: 1,
  },
  icon: {
    height: 20,
    fill: 'gray',
    color: 'gray',
    transform: 'rotateX( 0deg )',
    transition: 'transform .3s linear',
    display: 'flex',
    alignItems: 'center',
  },
  iconRotate: {
    transform: 'rotateX(180deg)',
  },
  container: {
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    flexDirection: 'row',
    backgroundColor: 'rgba(208, 208, 208, 0.13)',
    borderRadius: 10,
    transition: 'background-color .3s linear',
    minWidth: 'max-content',

    '&:hover': {
      transition: 'background-color .3s linear',
      backgroundColor: 'rgba(208,208,208,0.24)',
    },
  },
  row: {
    fontSize: 14,
  },
  containerFlex: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'baseline',
    flexDirection: 'row',
    padding: '0px 6px 0px 14px',
  },
  titleGray: {
    fontWeight: 400,
    color: 'gray',
    fontSize: '12px',
    marginRight: '8px',
  },
  titleBlack: {
    color: 'black',
    fontSize: '14px',
    marginRight: '2px',
  },
  chevron: {
    borderLeft: '1px solid rgb(218, 218, 218)',
    display: 'flex',
    height: '100%',
    padding: '8px',
  },
  chevronIcon: {
    width: 16,
    height: 16,
    fill: 'gray',
    color: 'gray',
    transform: 'rotate( 0deg )',
    transition: 'transform .3s linear',
  },
  tabIcon: {
    width: 16,
    marginLeft: 2,
  },
  tabIconButton: {
    padding: '4px 8px',
    width: '100%',
    fontSize: 12,
    lineHeight: 1.9,
  },
  rowsContainer: {
    overflow: 'scroll',
    maskImage: 'linear-gradient(180deg, rgb(0, 0, 0) 95%, transparent)',
  },
  button: {
    width: '100%',
  },
  buttonContainer: {
    padding: '16px 0px 0px 0px',
    display: 'flex',
    justifyContent: 'center',
  },
}));

const SortSelector = ({
                        sorting,
                        setSorting,
                        columns,
                      }) => {
  if (!sorting?.length) {
    return <></>;
  }

  const { en: { btn, tables } } = useSelector((state) => state.i18n);
  const classes = useStyles();
  const { id: sortKey, desc } = sorting[0];
  const [_desc, _setDesc] = useState(desc);
  const [_sortKey, _setSortKey] = useState(sortKey);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    _setDesc(desc);
    _setSortKey(sortKey);
  };

  const applySorting = () => {
    setAnchorEl(null);
    setSorting(pr => [{
      id: _sortKey,
      desc: _desc,
    }]);
  };

  useEffect(() => {
    const { id: sortKey, desc } = sorting[0];
    _setDesc(desc);
    _setSortKey(sortKey);
  }, [sorting]);

  const sortColumnName = useMemo(
    () => {
      return columns.find(columns => columns.accessorKey === sortKey)?.header;
    },
    [columns, sorting],
  );

  const modalItems = useMemo(
    () => {
      return columns.sort((a, b) => {
        const nameA = a.header.toUpperCase();
        const nameB = b.header.toUpperCase();
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      }).map(({ accessorKey, header }) => ({
        accessorKey,
        header,
      }));
    },
    [columns],
  );

  return <>
    <div className={classes.container} onClick={handleClick} >
      <div className={classes.containerFlex} >
        <div className={classes.titleGray}>{tables.sortedBy}</div>
        <div className={classes.titleBlack}><FadingText newText={sortColumnName}/></div>
      </div>
      <div id='basic-button'
           aria-controls={open ? 'basic-menu' : undefined}
           aria-haspopup='true'
           aria-expanded={open ? 'true' : undefined}
           className={classes.chevron}>
        <KeyboardArrowDownIcon className={classes.chevronIcon} />
      </div>
    </div>
    <Popover
      id='basic-menu'
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      getContentAnchorEl={null}
      slotProps={{ list: { 'aria-labelledby': 'basic-button' } }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <div className={classes.modal}>
        <div className={classes.modalTitle}>
          <div>{tables.sortTableBy}</div>
          <ToggleButtonGroup
            value={_desc}
            exclusive
            onChange={(e, v) => _setDesc(v)}
          >
            <ToggleButton className={classes.tabIconButton} value={true}>
              {tables.desc}
              <SortDescIcon className={classes.tabIcon} />
            </ToggleButton>
            <ToggleButton className={classes.tabIconButton} value={false}>
              {tables.asc}
              <SortAscIcon className={classes.tabIcon} />
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
        <div className={classes.rowsContainer}>
          <RadioGroup
            value={_sortKey}
            onChange={(e, v) => _setSortKey(v)}
          >
            {
              modalItems.map(item => <FormControlLabel value={item.accessorKey}
                                                       control={<Radio />}
                                                       classes={{ label: classes.row }}
                                                       label={item.header} />)
            }
          </RadioGroup>
        </div>
        <div className={classes.buttonContainer}>
          <Button
            color='primary'
            disabled={false}
            variant={'contained'}
            className={classes.button}
            onClick={applySorting}
          >
            {btn.applySorting}
          </Button>
        </div>
      </div>
    </Popover>
  </>;
};

export default SortSelector;
